(() => {
  if (customElements.get('bdg-size-guide')) return;

  class BdgSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.BdgSizeGuide__trigger');
      this.scroll = this.querySelector('.BdgSizeGuide__scroll');
      this.sizeList = this.closest('.ProductForm__Option')?.querySelector('.SizeSwatchList');
      if (!this.dialog || !this.trigger || !this.scroll) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('.BdgSizeGuide__close').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), summary, [tabindex="0"], a[href]'))
          .filter(element => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
      this.dialog.addEventListener('close', () => this.cleanupOpenState(), options);
      this.scroll.addEventListener('scroll', () => this.updateFades(), { ...options, passive: true });
      this.observer = new ResizeObserver(() => {
        this.updateFades();
        this.alignTrigger();
      });
      this.observer.observe(this.scroll);
      this.observer.observe(this.scroll.querySelector('table'));
      if (this.sizeList) {
        this.observer.observe(this.sizeList);
        this.sizeList.querySelectorAll('.SizeSwatch').forEach(swatch => this.observer.observe(swatch));
        this.alignTrigger();
      }
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.finishClose();
      }, options);
    }

    open() {
      if (this.dialog.open) return;
      document.querySelectorAll('bdg-size-guide').forEach((guide) => {
        if (guide !== this && guide.dialog?.open) guide.finishClose();
      });
      this.lastFocus = document.activeElement;
      this.dialog.showModal();
      document.documentElement.classList.add('bdg-size-guide-open');
      this.frame = requestAnimationFrame(() => {
        // Paint the newly visible dialog off-screen before starting its transition.
        this.frame = requestAnimationFrame(() => {
          if (!this.dialog.open) return;
          this.dialog.classList.add('is-open');
          this.updateFades();
        });
      });
    }

    close() {
      if (!this.dialog.open || this.closeTimer) return;
      cancelAnimationFrame(this.frame);
      this.dialog.classList.remove('is-open');
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finishClose();
      } else {
        this.closeTimer = setTimeout(() => this.finishClose(), 400);
      }
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
      cancelAnimationFrame(this.frame);
      this.dialog?.classList.remove('is-open');
      if (this.dialog?.open) this.dialog.close();
      this.cleanupOpenState();
    }

    cleanupOpenState() {
      if (!document.querySelector('.BdgSizeGuide[open]')) {
        document.documentElement.classList.remove('bdg-size-guide-open');
      }
      if (this.lastFocus?.isConnected && !document.querySelector('.BdgSizeGuide[open]')) {
        this.lastFocus.focus({ preventScroll: true });
      }
      this.lastFocus = null;
    }

    alignTrigger() {
      if (!this.sizeList || !this.parentElement.matches('.ProductForm__Label')) return;
      const swatches = Array.from(this.sizeList.querySelectorAll('.SizeSwatch'))
        .filter(swatch => swatch.getClientRects().length > 0);
      if (!swatches.length) return;
      // Wrapped rows can leave unused space to the right of the last column.
      const rightEdge = Math.max(...swatches.map(swatch => swatch.getBoundingClientRect().right));
      const labelRight = this.parentElement.getBoundingClientRect().right;
      this.style.marginRight = `${Math.max(0, labelRight - rightEdge)}px`;
    }

    updateFades() {
      const { scrollLeft, scrollWidth, clientWidth } = this.scroll;
      this.scroll.parentElement.classList.toggle('has-left-overflow', scrollLeft > 2);
      this.scroll.parentElement.classList.toggle('has-right-overflow', scrollWidth - clientWidth - scrollLeft > 2);
    }

    disconnectedCallback() {
      this.finishClose();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  }

  customElements.define('bdg-size-guide', BdgSizeGuide);
})();
